import { ComponentType } from "react";
import { Box, Cloud, Database } from "lucide-react";
import {
    SiAngular,
    SiDocker,
    SiDotnet,
    SiFirebase,
    SiGraphql,
    SiJavascript,
    SiJsonwebtokens,
    SiMongodb,
    SiMysql,
    SiNextdotjs,
    SiNodedotjs,
    SiPostgresql,
    SiPython,
    SiReact,
    SiRedis,
    SiSharp,
    SiSpringboot,
    SiTailwindcss,
    SiTypescript,
    SiVuedotjs,
} from "react-icons/si";
import { readFolder } from "../content/load";

export interface Tech {
    name: string;
    /*
     * Not React.ElementType. @react-three/fiber (pulled in by the Lanyard
     * scene elsewhere in src/) globally augments JSX.IntrinsicElements with
     * every Three.js class as a lowercase tag. ElementType resolves against
     * that same global IntrinsicElements map, so once it's inflated with
     * hundreds of non-DOM tags, checking a prop like `className` against
     * "every element type" collapses to `never` project-wide — including
     * here, which never touches Three.js. Naming the exact shape used below
     * sidesteps that global map entirely.
     */
    icon: ComponentType<{ className?: string }>;
}

/**
 * The project drawn as a system diagram by the Work scene, in the scene's
 * 1200×720 design space. Every tech tag on the card flies to a `tagTargets`
 * entry: a node id, `badge:<node>` (a small badge on that node's corner) or
 * `boundary` (the label of the box the system is deployed in).
 */
export interface Schematic {
    nodes: { id: string; x: number; y: number; role: string; label?: string; external?: boolean }[];
    tagTargets: Record<string, string>;
    /** SVG path data, drawn in this order. */
    edges: { d: string; label?: string; lx?: number; ly?: number }[];
    /** One per highlight, same order: badge position and caption position. */
    annotations: { short: string; text: string; bx: number; by: number; tx: number; ty: number }[];
}

export interface Project {
    slug: string;
    title: string;
    role: string;
    period: string;
    description: string;
    /** One line, used once the card has unfolded into the diagram. */
    summary: string;
    highlights: string[];
    tech: Tech[];
    repos: { label: string; url: string }[];
    featured?: boolean;
    schematic: Schematic;
}

/* ---------- content file shape (src/content/projects/*.json, edited at /admin) ---------- */

interface ProjectFile {
    order?: number;
    title: string;
    role?: string;
    period?: string;
    featured?: boolean;
    description?: string;
    summary?: string;
    highlights?: { text: string; short?: string; note?: string }[];
    tech?: string[];
    repos?: { label: string; url: string }[];
}

/* ---------- tech icons, matched by name ---------- */

const ICONS: Record<string, Tech["icon"]> = {
    ".net": SiDotnet,
    "asp.net": SiDotnet,
    "c#": SiSharp,
    "next.js": SiNextdotjs,
    react: SiReact,
    "sql server": Database,
    sql: Database,
    mongodb: SiMongodb,
    azure: Cloud,
    aws: Cloud,
    jwt: SiJsonwebtokens,
    docker: SiDocker,
    vue: SiVuedotjs,
    "node.js": SiNodedotjs,
    typescript: SiTypescript,
    javascript: SiJavascript,
    postgresql: SiPostgresql,
    mysql: SiMysql,
    redis: SiRedis,
    tailwind: SiTailwindcss,
    angular: SiAngular,
    "spring boot": SiSpringboot,
    python: SiPython,
    firebase: SiFirebase,
    graphql: SiGraphql,
};

function iconFor(name: string): Tech["icon"] {
    const key = name.trim().toLowerCase();
    if (ICONS[key]) return ICONS[key];
    // ".NET 8" → ".net", "Vue 3" → "vue": try the name without a version.
    const bare = key.replace(/\s*\d+(\.\d+)*$/, "");
    if (ICONS[bare]) return ICONS[bare];
    const hit = Object.keys(ICONS).find((k) => key.indexOf(k) === 0);
    return hit ? ICONS[hit] : Box;
}

/* ---------- diagrams ---------- */

type Layout = Omit<Schematic, "annotations"> & {
    /** Positions only; the words come from each highlight's `short` / `note`. */
    annotations: { bx: number; by: number; tx: number; ty: number; short: string; text: string }[];
};

/** Hand-placed diagrams, keyed by content file name. */
const LAYOUTS: Record<string, Layout> = {
    "ai-english-center": {
        nodes: [
            { id: "web", x: 545, y: 345, role: "Client" },
            { id: "api", x: 775, y: 345, role: "API" },
            { id: "auth", x: 775, y: 230, role: "Auth" },
            { id: "sql", x: 1010, y: 270, role: "Relational" },
            { id: "mongo", x: 1010, y: 420, role: "Document" },
            { id: "face", x: 775, y: 600, role: "External", label: "Face API", external: true },
        ],
        tagTargets: {
            ".NET 8": "api",
            "C#": "badge:api",
            "Next.js": "web",
            "SQL Server": "sql",
            MongoDB: "mongo",
            Azure: "boundary",
            JWT: "auth",
        },
        edges: [
            { d: "M615 345 H705", label: "REST · OData", lx: 660, ly: 322 },
            { d: "M775 320 V255" },
            { d: "M845 345 C892 345 892 270 940 270", label: "EF Core", lx: 905, ly: 252 },
            { d: "M845 345 C892 345 892 420 940 420" },
            { d: "M775 370 V575", label: "HTTPS", lx: 800, ly: 548 },
        ],
        annotations: [
            { short: "OData", text: "OData filtering, sorting & paging run on the server", bx: 651, by: 336, tx: 618, ty: 384 },
            { short: "JWT + RBAC", text: "JWT auth with role-based access control", bx: 836, by: 196, tx: 866, ty: 194 },
            { short: "QR + Face ID", text: "QR check-in verified by a face-recognition API", bx: 686, by: 591, tx: 482, ty: 582 },
            { short: "EF Core", text: "Eager loading + projection, N+1 removed", bx: 883, by: 298, tx: 905, ty: 322 },
        ],
    },
    taskify: {
        nodes: [
            { id: "web", x: 545, y: 345, role: "Client" },
            { id: "api", x: 775, y: 345, role: "API" },
            { id: "auth", x: 775, y: 230, role: "Auth" },
            { id: "sql", x: 1010, y: 345, role: "Relational" },
        ],
        tagTargets: {
            ".NET 8": "api",
            "C#": "badge:api",
            React: "web",
            "SQL Server": "sql",
            JWT: "auth",
            Docker: "boundary",
        },
        edges: [
            { d: "M615 345 H705", label: "REST · OData", lx: 660, ly: 322 },
            { d: "M775 320 V255" },
            { d: "M845 345 H940", label: "EF Core", lx: 892, ly: 330 },
        ],
        annotations: [
            { short: "C-S-R + DI", text: "Controller–Service–Repository with dependency injection", bx: 697, by: 311, tx: 540, ty: 262 },
            { short: "OData", text: "OData filtering, sorting & paging at the database layer", bx: 651, by: 336, tx: 618, ty: 384 },
            { short: "JWT + RBAC", text: "JWT auth with role-based, multi-tenant access", bx: 836, by: 196, tx: 866, ty: 194 },
            { short: "Docker", text: "Dockerized stack: the same environment from dev to prod", bx: 548, by: 161, tx: 470, ty: 192 },
        ],
    },
};

/** Grid columns/rows of the generated diagram, inside the deploy boundary. */
const GRID_X = [545, 775, 1010];
const GRID_Y = [230, 360, 490];

/**
 * A diagram for a project added through the CMS: one node per tech, up to
 * nine, on a 3×3 grid, wired to their grid neighbours; each highlight pinned
 * to the node in the same position.
 */
function autoLayout(tech: string[], count: number): Layout {
    const names = tech.slice(0, GRID_X.length * GRID_Y.length);
    const nodes = names.map((name, i) => ({
        id: `n${i}`,
        x: GRID_X[i % GRID_X.length],
        y: GRID_Y[Math.floor(i / GRID_X.length)],
        role: "",
    }));
    const tagTargets: Record<string, string> = {};
    names.forEach((name, i) => (tagTargets[name] = `n${i}`));
    const edges: Layout["edges"] = [];
    nodes.forEach((n, i) => {
        const right = nodes[i + 1];
        if (right && right.y === n.y) edges.push({ d: `M${n.x + 70} ${n.y} H${right.x - 70}` });
        const below = nodes[i + GRID_X.length];
        if (below) edges.push({ d: `M${n.x} ${n.y + 25} V${below.y - 25}` });
    });
    const annotations = Array.from({ length: Math.min(count, nodes.length) }, (_, i) => {
        const n = nodes[i];
        return { short: "", text: "", bx: n.x + 54, by: n.y - 34, tx: n.x - 70, ty: n.y + 30 };
    });
    return { nodes, tagTargets, edges, annotations };
}

function toProject(slug: string, f: ProjectFile): Project {
    const highlights = f.highlights ?? [];
    const techNames = f.tech ?? [];
    const layout = LAYOUTS[slug] ?? autoLayout(techNames, highlights.length);
    // The card has room for four highlight lines, so the diagram pins four.
    const annotations = layout.annotations.slice(0, Math.min(4, highlights.length)).map((a, i) => {
        const h = highlights[i];
        return {
            ...a,
            short: h.short || a.short || `Point ${i + 1}`,
            text: h.note || a.text || h.text,
        };
    });
    return {
        slug,
        title: f.title,
        role: f.role ?? "",
        period: f.period ?? "",
        featured: !!f.featured,
        description: f.description ?? "",
        summary: f.summary || f.description || "",
        highlights: highlights.map((h) => h.text),
        tech: techNames.map((name) => ({ name, icon: iconFor(name) })),
        repos: f.repos ?? [],
        schematic: { ...layout, annotations },
    };
}

export const PROJECTS: Project[] = readFolder<ProjectFile>(require.context("../content/projects", false, /\.json$/))
    .sort((a, b) => (a.data.order ?? 999) - (b.data.order ?? 999))
    .map(({ slug, data }) => toProject(slug, data));
